// src/pages/NewTrade.jsx
// Form uses uncontrolled inputs (refs) so typing NEVER triggers re-renders = no focus loss
import React, { useRef, useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, PlusCircle, LineChart, Activity } from 'lucide-react';
import { validateTradeLogic, calculateExpectedPnl } from '../utils/tradeValidation';

const PAIRS = [
  // Forex Majors
  'EUR/USD','GBP/USD','USD/JPY','AUD/USD','NZD/USD','USD/CAD','USD/CHF',
  // Forex Crosses
  'EUR/JPY','GBP/JPY','EUR/GBP','GBP/AUD','EUR/AUD','EUR/CAD',
  // Metals & Oil
  'XAU/USD','XAG/USD','USOIL','UKOIL',
  // Indices (CFD)
  'NAS100','US30','SPX500',
  // Futures
  'GC (Gold)','MGC (Micro Gold)','CL (Crude Oil)',
  'NQ','MNQ','ES','MES','YM','MYM',
  // Crypto
  'BTC/USD','ETH/USD',
  'Other'
];

// Futures symbols for detecting futures trades
const FUTURES_SYMBOLS = ['GC (Gold)','MGC (Micro Gold)','CL (Crude Oil)','NQ','MNQ','ES','MES','YM','MYM'];
const SESSIONS   = ['London','New York','Asian','London/NY Overlap','Asian/London Overlap'];
const EMOTIONS   = ['Calm','Confident','Neutral','Excited','Anxious','Fearful','Greedy','Frustrated','Angry','Impatient','Bored'];
const MISTAKES   = ['None','Impatience','Revenge Trading','Overtrading','Adding to Loser','Chasing Entry','Broke SL','Broke TP','Sized Too Big','Overleveraged','FOMO','No Setup / No Confirmation','Other'];
const ORDER_FLOWS   = ['','Trend','Counter Trend','Ranging Market','Breakout','Reversal'];
const ANALYSIS_TFS  = ['','1M','5M','15M','30M','1H','4H','Daily','Weekly'];
const ACCOUNTS   = ['MT5 COINEXX','MT4','Prop Firm','Paper Trading','Other'];
const POI_OPTIONS = [
  '','OB- BOS & RTST','OB -TOOK LQ- BOS & RTST','Order Block (OB)',
  'Break of Structure (BOS)','Liquidity Sweep','Candle Rejection',
  'Candle Close Confirmation','NY Session Setup','London Session Setup',
  'Asian Range Breakout','Support / Resistance','Fair Value Gap (FVG)',
  'Inducement + Sweep','No Confirmation - No Setup','Counter Trend Setup',
  'Ranging Market Entry','Other'
];
const CHECKLIST = [
  { id: 'setup',     label: 'I SAW MY SETUP — I waited for full confirmation' },
  { id: 'risk',      label: 'My risk is defined and within my per-trade limit' },
  { id: 'sl',        label: 'My stop loss is valid and placed correctly' },
  { id: 'chasing',   label: 'I am NOT chasing — the move is not gone' },
  { id: 'revenge',   label: 'I am NOT revenge trading' },
  { id: 'dailyloss', label: 'I have NOT hit my daily loss limit' },
  { id: 'clarity',   label: 'I am calm and in the right mental state to trade' },
];

// ── helpers defined OUTSIDE so React never remounts inputs ──────────
function Field({ label, error, sub, children }) {
  return (
    <div>
      <label style={{
        fontSize: 11, color: error ? '#ff4d6d' : '#8888a8',
        fontFamily: "'DM Mono', monospace", textTransform: 'uppercase',
        letterSpacing: '0.07em', display: 'block', marginBottom: 6
      }}>
        {label}{error && <span style={{ marginLeft: 6, fontSize: 10 }}>({error})</span>}
      </label>
      {children}
      {sub && (
        <div style={{
          fontSize: 10, color: '#55556a', fontFamily: "'DM Mono', monospace",
          marginTop: 4, letterSpacing: '0.02em',
        }}>{sub}</div>
      )}
    </div>
  );
}

// Small stat tile used in the live calc preview under "Prices & Risk"
function PreviewStat({ label, value, color = '#f0f0f5' }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: '#55556a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color }}>
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 12, padding: 22, marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#f0f0f5', marginBottom: 18, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Mono', monospace" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14 };
const gridClassName = 'newtrade-grid';

// ── Main component ───────────────────────────────────────────────────
export default function NewTrade({ onAdd, settings, accounts = [], activeAccountId , lang = 'en'}) {
  const today = new Date().toISOString().slice(0, 10);

  // Uncontrolled refs for every text/number input — no re-render on keystroke
  const refs = {
    lotSize:       useRef(null),
    contracts:     useRef(null),
    date:          useRef(null),
    pairCustom:    useRef(null),
    entryPrice:    useRef(null),
    stopLoss:      useRef(null),
    takeProfit:    useRef(null),
    riskAmount:    useRef(null),
    rrAchieved:    useRef(null),
    pnl:           useRef(null),
    screenshotLink:useRef(null),
    notes:         useRef(null),
    poiCustom:     useRef(null),
  };

  // Only dropdowns and checklist need state (they don't have typing)
  const [pair,         setPair]         = useState('XAU/USD');
  const isFutures = FUTURES_SYMBOLS.includes(pair);
  // Status derived from result — 'Ongoing' means open trade

  // Sync account when activeAccountId changes
  useEffect(() => {
    if (activeAccountId) setAccount(activeAccountId);
  }, [activeAccountId]);
  const [session,      setSession]      = useState('London');
  const [direction,    setDirection]    = useState('Buy');
  const [result,       setResult]       = useState('Win');
  const [mistakeMade,  setMistakeMade]  = useState('None');
  const [emotionBefore,setEmotionBefore]= useState('Calm');
  const [emotionAfter, setEmotionAfter] = useState('Calm');
  const [account,      setAccount]      = useState(activeAccountId || '');
  const [poi,          setPoi]          = useState('');
  const [orderFlow,    setOrderFlow]    = useState('');
  const [analysisTF,   setAnalysisTF]   = useState('');
  const [checklist,    setChecklist]    = useState({});
  const [errors,       setErrors]       = useState({});
  const [submitted,    setSubmitted]    = useState(false);
  const [logicErrors,  setLogicErrors]  = useState([]);
  const [logicWarnings,setLogicWarnings]= useState([]);
  const [expectedPnl,  setExpectedPnl]  = useState(null);
  // Bumps on each price/lot blur to force the live preview to recompute.
  // Inputs themselves stay uncontrolled (no re-render on keystroke).
  const [previewTick,  setPreviewTick]  = useState(0);
  const bumpPreview = () => setPreviewTick(t => t + 1);

  const allChecked     = CHECKLIST.every(i => checklist[i.id]);
  const uncheckedCount = CHECKLIST.filter(i => !checklist[i.id]).length;
  const toggleCheck    = (id) => setChecklist(c => ({ ...c, [id]: !c[id] }));

  // Auto-calc RR from entry/sl/tp — runs on blur, no state = no focus loss
  const calcRR = () => {
    const entry = parseFloat(refs.entryPrice.current?.value);
    const sl    = parseFloat(refs.stopLoss.current?.value);
    const tp    = parseFloat(refs.takeProfit.current?.value);
    if (!entry || !sl || !tp || Math.abs(entry - sl) === 0) return;
    const rr = (Math.abs(tp - entry) / Math.abs(entry - sl)).toFixed(2);
    if (refs.rrAchieved.current) refs.rrAchieved.current.value = rr;
  };

  // Auto-calc P&L from rr + risk — runs on blur
  const calcPnl = () => {
    const rr   = parseFloat(refs.rrAchieved.current?.value);
    const risk = parseFloat(refs.riskAmount.current?.value || settings.riskPerTrade || 100);
    if (!risk || isNaN(rr)) return;
    if (result === 'Ongoing') {
      if (refs.pnl.current) refs.pnl.current.value = '0';
      return;
    }
    const pnl = result === 'Win'  ? (rr * risk).toFixed(2)
              : result === 'Loss' ? (-risk).toFixed(2)
              : '0';
    if (refs.pnl.current) refs.pnl.current.value = pnl;
  };

  // Auto-calc Risk Amount from lot/contracts + entry + SL + pair — runs on blur.
  // Uses the same math as calculateExpectedPnl but applied to the SL distance,
  // which gives the dollar amount at risk if SL hits.
  const calcRiskAmount = () => {
    const entry = parseFloat(refs.entryPrice.current?.value);
    const sl    = parseFloat(refs.stopLoss.current?.value);
    if (!entry || !sl || entry === sl) return;
    const lotSize   = parseFloat(refs.lotSize.current?.value) || 0;
    const contracts = parseFloat(refs.contracts.current?.value) || 0;
    // Pretend this is a Loss scenario to get the SL-distance dollar amount.
    const probe = calculateExpectedPnl({
      pair, direction, entryPrice: entry, stopLoss: sl, takeProfit: entry,
      lotSize, contracts, result: 'Loss',
    });
    if (probe === null || isNaN(probe)) return;
    const risk = Math.abs(probe);
    if (risk > 0 && refs.riskAmount.current) {
      refs.riskAmount.current.value = risk.toFixed(2);
    }
  };

  // Lightweight live preview — computed inline on each render from
  // current ref values. Cheap because we only re-render on dropdown
  // change, not on every keystroke.
  const livePreview = (() => {
    const entry = parseFloat(refs.entryPrice.current?.value);
    const sl    = parseFloat(refs.stopLoss.current?.value);
    const tp    = parseFloat(refs.takeProfit.current?.value);
    const lots  = parseFloat(refs.lotSize.current?.value)   || 0;
    const ct    = parseFloat(refs.contracts.current?.value) || 0;
    if (!entry || !sl || entry === sl) return null;

    const calculatedRR = tp ? (Math.abs(tp - entry) / Math.abs(entry - sl)) : null;

    const slRiskProbe = calculateExpectedPnl({
      pair, direction, entryPrice: entry, stopLoss: sl, takeProfit: entry,
      lotSize: lots, contracts: ct, result: 'Loss',
    });
    const tpProbe = tp ? calculateExpectedPnl({
      pair, direction, entryPrice: entry, stopLoss: sl, takeProfit: tp,
      lotSize: lots, contracts: ct, result: 'Win',
    }) : null;

    return {
      rr: calculatedRR,
      riskDollars: slRiskProbe !== null ? Math.abs(slRiskProbe) : null,
      rewardDollars: tpProbe !== null ? Math.abs(tpProbe) : null,
    };
  })();

  const getVal = (ref) => ref.current?.value ?? '';

  const validate = () => {
    const e = {};
    if (!pair) e.pair = 'Required';
    if (pair === 'Other' && !getVal(refs.pairCustom)) e.pairCustom = 'Enter pair name';
    if (result !== 'Ongoing' && !getVal(refs.pnl)) e.pnl = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const finalPair = pair === 'Other' ? (getVal(refs.pairCustom) || 'Other') : pair;
    const finalPoi  = poi  === 'Other' ? (getVal(refs.poiCustom)  || 'Other') : poi;

    // Build trade object for logic validation
    const tradeForCheck = {
      pair: finalPair, direction, result,
      entryPrice: parseFloat(getVal(refs.entryPrice)) || 0,
      stopLoss:   parseFloat(getVal(refs.stopLoss))   || 0,
      takeProfit: parseFloat(getVal(refs.takeProfit)) || 0,
      lotSize:    isFutures ? 0 : (parseFloat(getVal(refs.lotSize)) || 0),
      contracts:  isFutures ? (parseFloat(getVal(refs.contracts)) || 0) : 0,
      pnl:        parseFloat(getVal(refs.pnl)) || 0,
      rrAchieved: parseFloat(getVal(refs.rrAchieved)) || 0,
      status:     result === 'Ongoing' ? 'open' : 'closed',
    };

    const check = validateTradeLogic(tradeForCheck);
    setLogicErrors(check.errors);
    setLogicWarnings(check.warnings);
    setExpectedPnl(check.expectedPnl);

    // Block submission on hard errors
    if (!check.valid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // For warnings — first click shows warnings, second click submits
    if (check.warnings.length > 0 && logicWarnings.length === 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    onAdd({
      date:          getVal(refs.date) || today,
      pair:          finalPair,
      session, direction, result, mistakeMade,
      emotionBefore, emotionAfter, account,
      poi:           finalPoi,
      orderFlow, analysisTF,
      entryPrice:    parseFloat(getVal(refs.entryPrice))   || 0,
      stopLoss:      parseFloat(getVal(refs.stopLoss))     || 0,
      takeProfit:    parseFloat(getVal(refs.takeProfit))   || 0,
      riskAmount:    parseFloat(getVal(refs.riskAmount))   || 0,
      lotSize:       isFutures ? 0 : (parseFloat(getVal(refs.lotSize)) || 0),
      contracts:     isFutures ? (parseFloat(getVal(refs.contracts)) || 0) : 0,
      pnl:           parseFloat(getVal(refs.pnl))          || 0,
      rrAchieved:    parseFloat(getVal(refs.rrAchieved))   || 0,
      screenshotLink:getVal(refs.screenshotLink),
      notes:         getVal(refs.notes),
      followedRules: allChecked,
      status: result === 'Ongoing' ? 'open' : 'closed',
    });
    setSubmitted(true);
    // Reset
    Object.values(refs).forEach(r => { if (r.current) r.current.value = ''; });
    if (refs.date.current) refs.date.current.value = today;
    setPair('XAU/USD'); setSession('London'); setDirection('Buy');
    setResult('Win'); setMistakeMade('None'); setEmotionBefore('Calm');
    setEmotionAfter('Calm'); setAccount(activeAccountId || '');
    setLogicErrors([]); setLogicWarnings([]); setExpectedPnl(null);
    setPoi(''); setOrderFlow(''); setAnalysisTF('');
    setChecklist({});
    setTimeout(() => setSubmitted(false), 2500);
  };

  const inputStyle = {
    background: '#0a0a0f', border: '1px solid #1e1e2e', borderRadius: 8,
    color: '#f0f0f5', padding: '10px 14px',
    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14,
    width: '100%', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const onFocus = e => e.target.style.borderColor = '#00e5a0';
  const onBlurStyle = e => e.target.style.borderColor = '#1e1e2e';

  return (
    <div className="fade-in" style={{ padding: '28px 32px', maxWidth: 920, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: '#f0f0f5', margin: 0 }}>New Trade</h1>
        <p style={{ color: '#ffd166', fontSize: 12, margin: '6px 0 0', fontFamily: "'DM Mono', monospace", letterSpacing: '0.04em' }}>
          SEE SETUP. WAIT FOR CONFIRMATION. DEFINE RISK & GO.
        </p>
      </div>

      {/* Validation errors — block submit */}
      {logicErrors.length > 0 && (
        <div style={{ background:'rgba(255,77,109,0.08)', border:'1px solid rgba(255,77,109,0.3)', borderRadius:12, padding:'14px 18px', marginBottom:14 }}>
          <div style={{ fontSize:11, color:'#ff4d6d', fontWeight:700, fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, display:'inline-flex', alignItems:'center', gap:6 }}>
            <XCircle size={12} /> Trade can't be logged — fix these issues first
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {logicErrors.map((err, i) => (
              <div key={i} style={{ fontSize:13, color:'#f0f0f5', lineHeight:1.6, display:'flex', gap:8, alignItems:'flex-start' }}>
                <span style={{ color:'#ff4d6d', flexShrink:0 }}>•</span> {err}
              </div>
            ))}
          </div>
          {expectedPnl !== null && (
            <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid rgba(255,77,109,0.15)', fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace" }}>
              Calculated expected P&L: ${expectedPnl.toFixed(2)} based on your entry, exit price, and {expectedPnl !== null ? 'position size' : ''}
            </div>
          )}
        </div>
      )}

      {/* Validation warnings — allow submit but flag */}
      {logicWarnings.length > 0 && logicErrors.length === 0 && (
        <div style={{ background:'rgba(255,209,102,0.06)', border:'1px solid rgba(255,209,102,0.25)', borderRadius:12, padding:'14px 18px', marginBottom:14 }}>
          <div style={{ fontSize:11, color:'#ffd166', fontWeight:700, fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, display:'inline-flex', alignItems:'center', gap:6 }}>
            <AlertTriangle size={12} /> Review before saving
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {logicWarnings.map((w, i) => (
              <div key={i} style={{ fontSize:13, color:'#f0f0f5', lineHeight:1.6, display:'flex', gap:8, alignItems:'flex-start' }}>
                <span style={{ color:'#ffd166', flexShrink:0 }}>•</span> {w}
              </div>
            ))}
          </div>
          <div style={{ marginTop:10, fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace" }}>
            Click submit again to save anyway, or fix the values.
          </div>
        </div>
      )}

      {/* Checklist */}
      <div style={{ background: '#16161f', border: `1px solid ${allChecked ? 'rgba(0,229,160,0.25)' : '#1e1e2e'}`, borderRadius: 12, padding: 22, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <AlertTriangle size={14} style={{ color: '#ffd166' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#f0f0f5', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Mono', monospace" }}>Pre-Trade Checklist</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: allChecked ? '#00e5a0' : '#ffd166', fontFamily: "'DM Mono', monospace" }}>
            {CHECKLIST.length - uncheckedCount}/{CHECKLIST.length}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {CHECKLIST.map(item => {
            const checked = checklist[item.id];
            return (
              <button key={item.id} type="button" onClick={() => toggleCheck(item.id)} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: checked ? 'rgba(0,229,160,0.06)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${checked ? 'rgba(0,229,160,0.2)' : '#1e1e2e'}`,
                borderRadius: 7, padding: '9px 12px', cursor: 'pointer',
                textAlign: 'left', transition: 'all 0.15s', width: '100%', outline: 'none'
              }}>
                {checked ? <CheckCircle size={14} style={{ color: '#00e5a0', flexShrink: 0 }} />
                         : <XCircle    size={14} style={{ color: '#2a2a3e', flexShrink: 0 }} />}
                <span style={{ fontSize: 12, color: checked ? '#f0f0f5' : '#8888a8' }}>{item.label}</span>
              </button>
            );
          })}
        </div>
        {!allChecked && (
          <div style={{ marginTop: 12, padding: '9px 12px', background: 'rgba(255,77,109,0.06)', border: '1px solid rgba(255,77,109,0.2)', borderRadius: 7 }}>
            <div style={{ fontSize: 11, color: '#ff4d6d', display:'inline-flex', alignItems:'center', gap:6 }}><AlertTriangle size={11} /> {uncheckedCount} item(s) unchecked — trade will be flagged as Rules Broken</div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Trade Details */}
        <Section title="Trade Details">
          <div className="newtrade-grid" style={grid}>
            <Field label="Date">
              <input ref={refs.date} defaultValue={today} type="date" style={inputStyle} onFocus={onFocus} onBlur={onBlurStyle} />
            </Field>
            <Field label="Pair / Symbol" error={errors.pair}>
              <select value={pair} onChange={e => setPair(e.target.value)} style={inputStyle}>
                {PAIRS.map(p => <option key={p}>{p}</option>)}
              </select>
            </Field>
            {pair === 'Other' && (
              <Field label="Enter Pair Name" error={errors.pairCustom}>
                <input ref={refs.pairCustom} type="text" placeholder="e.g. AUDCAD" style={inputStyle} onFocus={onFocus} onBlur={onBlurStyle} />
              </Field>
            )}
            <Field label="Account">
              <select value={account} onChange={e => setAccount(e.target.value)} style={inputStyle}>
                {accounts.length > 0
                  ? accounts.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name}{a.type ? ` (${a.type})` : ''}
                      </option>
                    ))
                  : <option value="">No accounts — create one first</option>
                }
              </select>
            </Field>
            <Field label="Session">
              <select value={session} onChange={e => setSession(e.target.value)} style={inputStyle}>
                {SESSIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Direction / Bias">
              <select value={direction} onChange={e => setDirection(e.target.value)} style={inputStyle}>
                <option>Buy</option><option>Sell</option>
              </select>
            </Field>
            <Field label="Analysis TF">
              <select value={analysisTF} onChange={e => setAnalysisTF(e.target.value)} style={inputStyle}>
                {ANALYSIS_TFS.map(tf => <option key={tf} value={tf}>{tf || '— select —'}</option>)}
              </select>
            </Field>
            <Field label="Order Flow">
              <select value={orderFlow} onChange={e => setOrderFlow(e.target.value)} style={inputStyle}>
                {ORDER_FLOWS.map(o => <option key={o} value={o}>{o || '— select —'}</option>)}
              </select>
            </Field>
            <Field label="POI / Setup">
              <select value={poi} onChange={e => setPoi(e.target.value)} style={inputStyle}>
                {POI_OPTIONS.map(p => <option key={p} value={p}>{p || '— select —'}</option>)}
              </select>
            </Field>
            {poi === 'Other' && (
              <Field label="Describe Setup">
                <input ref={refs.poiCustom} type="text" placeholder="e.g. Double Top + OB" style={inputStyle} onFocus={onFocus} onBlur={onBlurStyle} />
              </Field>
            )}
          </div>
        </Section>

        {/* Prices & Risk */}
        <Section title="Prices & Risk">
          <div className="newtrade-grid" style={grid}>
            <Field label="Entry Price">
              <input ref={refs.entryPrice} type="number" step="any" placeholder="e.g. 2325.50" style={inputStyle}
                onFocus={onFocus} onBlur={e => { onBlurStyle(e); calcRR(); calcRiskAmount(); bumpPreview(); }} />
            </Field>
            <Field label="Stop Loss">
              <input ref={refs.stopLoss} type="number" step="any" placeholder="e.g. 2318.00" style={inputStyle}
                onFocus={onFocus} onBlur={e => { onBlurStyle(e); calcRR(); calcRiskAmount(); bumpPreview(); }} />
            </Field>
            <Field label="Take Profit">
              <input ref={refs.takeProfit} type="number" step="any" placeholder="e.g. 2348.00" style={inputStyle}
                onFocus={onFocus} onBlur={e => { onBlurStyle(e); calcRR(); bumpPreview(); }} />
            </Field>
            {isFutures ? (
              <Field label="Contracts">
                <input ref={refs.contracts} type="number" step="1" placeholder="e.g. 1"
                  style={inputStyle} onFocus={onFocus} onBlur={e => { onBlurStyle(e); calcRiskAmount(); bumpPreview(); }} />
              </Field>
            ) : (
              <Field label="Lot Size">
                <input ref={refs.lotSize} type="number" step="0.01" placeholder="e.g. 0.10"
                  style={inputStyle} onFocus={onFocus} onBlur={e => { onBlurStyle(e); calcRiskAmount(); bumpPreview(); }} />
              </Field>
            )}
            <Field label="Risk Amount ($)" sub="Auto-fills from lot size + entry + SL">
              <input ref={refs.riskAmount} type="number" step="any" placeholder={`e.g. ${settings.riskPerTrade || 100}`}
                style={inputStyle} onFocus={onFocus} onBlur={onBlurStyle} />
            </Field>
          </div>

          {/* ── Live auto-calc preview ─────────────────────────────
              Updates whenever the user blurs out of an input above.
              Tries to read entry/SL/TP + lot/contracts and shows the
              calculated RR, dollar risk, and dollar reward — so you
              can sanity-check the trade BEFORE you submit. */}
          {livePreview && (livePreview.rr !== null || livePreview.riskDollars !== null) && (
            <div style={{
              marginTop: 14, padding: '12px 14px',
              background: 'rgba(0,229,160,0.04)',
              border: '1px solid rgba(0,229,160,0.18)',
              borderRadius: 9,
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12,
              fontFamily: "'DM Mono', monospace",
            }}>
              {livePreview.rr !== null && livePreview.rr > 0 && (
                <PreviewStat label="Calc R:R" value={`${livePreview.rr.toFixed(2)} : 1`} color="#00e5a0" />
              )}
              {livePreview.riskDollars !== null && livePreview.riskDollars > 0 && (
                <PreviewStat label="Risk on SL" value={`$${livePreview.riskDollars.toFixed(2)}`} color="#ff4d6d" />
              )}
              {livePreview.rewardDollars !== null && livePreview.rewardDollars > 0 && (
                <PreviewStat label="Reward at TP" value={`$${livePreview.rewardDollars.toFixed(2)}`} color="#00e5a0" />
              )}
            </div>
          )}
        </Section>

        {/* Result */}
        <Section title="Trade Status / Result">
          <div className="newtrade-grid" style={grid}>
            <Field label="Status / Result" sub="Choose Ongoing if trade is still open">
              <select value={result} onChange={e => setResult(e.target.value)} style={inputStyle}>
                <option>Win</option><option>Loss</option><option>Breakeven</option><option>Ongoing</option>
              </select>
            </Field>
            <Field label="RR Achieved">
              <input ref={refs.rrAchieved} type="number" step="any" placeholder="e.g. 2.0" style={inputStyle}
                onFocus={onFocus} onBlur={e => { onBlurStyle(e); calcPnl(); }} />
            </Field>
            {result === 'Ongoing' && (
              <div style={{ gridColumn:'1 / -1', fontSize:11, color:'#ffd166', fontFamily:"'DM Mono',monospace", padding:'10px 14px', background:'rgba(255,209,102,0.06)', border:'1px solid rgba(255,209,102,0.15)', borderRadius:8 }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><Activity size={12} /> Ongoing trade — won't affect win rate or P&L stats. Edit later from the Journal to close it with Win/Loss/BE.</span>
              </div>
            )}
            <Field label="P&L ($)" error={errors.pnl}>
              <input ref={refs.pnl} type="number" step="any" placeholder="e.g. 178 or -126" style={inputStyle}
                onFocus={onFocus} onBlur={onBlurStyle} />
            </Field>
            <Field label="Mistake Made">
              <select value={mistakeMade} onChange={e => setMistakeMade(e.target.value)} style={inputStyle}>
                {MISTAKES.map(m => <option key={m}>{m}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        {/* Chart, Emotions & Notes */}
        <Section title="Chart, Emotions & Notes">
          <Field label="TradingView Chart Link">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input ref={refs.screenshotLink} type="url" placeholder="https://www.tradingview.com/x/..."
                style={inputStyle} onFocus={onFocus} onBlur={onBlurStyle} />
            </div>
          </Field>
          <div style={{ ...grid, marginTop: 14, marginBottom: 14 }}>
            <Field label="Emotion Before">
              <select value={emotionBefore} onChange={e => setEmotionBefore(e.target.value)} style={inputStyle}>
                {EMOTIONS.map(em => <option key={em}>{em}</option>)}
              </select>
            </Field>
            <Field label="Emotion After">
              <select value={emotionAfter} onChange={e => setEmotionAfter(e.target.value)} style={inputStyle}>
                {EMOTIONS.map(em => <option key={em}>{em}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes / Comments">
            <textarea ref={refs.notes} rows={3}
              placeholder="What happened? What did you see? What would you do differently?"
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
              onFocus={onFocus} onBlur={onBlurStyle} />
          </Field>
        </Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="submit" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlusCircle size={15} />
            {submitted ? '✓ Trade Logged!' : 'Log Trade'}
          </button>
          {!allChecked && (
            <span style={{ fontSize: 12, color: '#ffd166', display:'inline-flex', alignItems:'center', gap:6 }}>
              <AlertTriangle size={12} /> {uncheckedCount} item(s) not confirmed — trade will be flagged
            </span>
          )}
          {submitted && <span style={{ fontSize: 13, color: '#00e5a0' }}>Saved to journal ✓</span>}
        </div>
      </form>
    </div>
  );
}
